// coolingDefaults — the active tenant's Precool prefill defaults (berry flow
// Tier 1, mig 20260729150622), resolved from packhouse_config.cooling_defaults:
//
//   { "target_temp_c":      { "<flow_key>": number },
//     "std_crate_gross_kg": { "<VARIETY>":  number } }
//
// Target temp is per CHANNEL (strawberries cool to a different setpoint than
// blueberries); standard crate weight is per VARIETY (decision 2026-07-29) and
// is GROSS per crate — the whole crate, tare deducted to net downstream, the
// same basis the intake weighing feature stores (mig 1045).
//
// These are PREFILLS, never gates: every resolved value lands in an editable
// field, and a malformed config entry resolves to null rather than throwing —
// the operator just types the number like before.

const asObj = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null

const asNum = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null

// L41: temperatures are the one cooling default where 0 and sub-zero are
// LEGITIMATE values (berry cool setpoints commonly sit at 0 °C or just
// below), so the shared > 0 guard — right for crate weights — silently
// dropped exactly the near-freezing lanes' configured prefill.
const asTempC = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/** Per-channel default target temperature, or null when unconfigured. */
export function defaultTargetTempC(config: unknown, flowKey: string | null | undefined): number | null {
  if (!flowKey) return null
  return asTempC(asObj(asObj(config)?.['target_temp_c'])?.[flowKey])
}

/** Per-variety standard GROSS crate weight (kg), or null when unconfigured.
 *  Variety keys are stored upper-cased (batches/deliveries upper-case on save);
 *  the lookup is case-insensitive so a hand-typed config key still matches. */
export function stdCrateGrossKg(config: unknown, variety: string | null | undefined): number | null {
  const table = asObj(asObj(config)?.['std_crate_gross_kg'])
  if (!table || !variety) return null
  const want = variety.trim().toUpperCase()
  if (!want) return null
  for (const [k, v] of Object.entries(table)) {
    if (k.trim().toUpperCase() === want) return asNum(v)
  }
  return null
}
